import { useId, type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { HiddenLegend, ManuallyLabeledRadio } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import { borderRadius, color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { styled } from '@/UI/theme.exports';
import { TextSmRg, TextStdMd, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { FlexCol } from '@/UI/styles';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { CheckMarkCircleFilledIcon } from '@/UI/Icons/CheckMarkCircleFilledIcon';

export interface CardRadioOption<V extends string> {
  value: V;
  title: string;
  subtitle?: ReactNode;
  content?: ReactNode;
}

interface CardRadioGroupProps<V extends string> {
  options: readonly CardRadioOption<V>[];
  selected: V;
  onSelect: (value: V) => void;
  groupLabel: string;
  title?: ReactNode;
}

/**
 * A single-select list of selectable cards. FPL-on renders an accessible `ManuallyLabeledRadio`
 * group where each card is a `<label>` wrapping a native radio input (the FPL radio icon sits at the
 * top-right); the selected card gets the `border-selected` token, the rest `border` (default).
 * FPL-off keeps the legacy `BlackRectangleButton` with the corner checkmark.
 */
export function CardRadioGroup<V extends string>({
  options,
  selected,
  onSelect,
  groupLabel,
  title,
}: CardRadioGroupProps<V>) {
  const isFpl = useFplMigrationFlag();

  const titleEl = title ? (
    <div style={{ marginLeft: isFpl ? '2px' : undefined }}>
      <TextStdRg color="inherit">{title}</TextStdRg>
    </div>
  ) : null;

  if (isFpl) {
    return (
      <>
        {titleEl}
        <ManuallyLabeledRadio.Root value={selected} onChange={(value) => onSelect(value as V)}>
          <HiddenLegend>{groupLabel}</HiddenLegend>
          <FlexCol sx={{ gap: 1, mt: 2 }}>
            {options.map((option) => (
              <CardRadioButton key={option.value} option={option} isSelected={selected === option.value} />
            ))}
          </FlexCol>
        </ManuallyLabeledRadio.Root>
      </>
    );
  }

  return (
    <>
      {titleEl}
      <FlexCol sx={{ gap: 1, mt: 4 }}>
        {options.map((option) => {
          const isSelected = selected === option.value;
          return (
            <BlackRectangleButton
              key={option.value}
              onClick={() => onSelect(option.value)}
              sx={getButtonStyles(isSelected)}
            >
              <CheckmarkIcon isVisible={isSelected} />
              <TextStdMd sx={{ mb: option.subtitle != null ? 0.5 : 0 }}>{option.title}</TextStdMd>
              {option.subtitle != null && <TextSmRg color={color.White80_T}>{option.subtitle}</TextSmRg>}
              {option.content}
            </BlackRectangleButton>
          );
        })}
      </FlexCol>
    </>
  );
}

function CardRadioButton<V extends string>({
  option,
  isSelected,
}: {
  option: CardRadioOption<V>;
  isSelected: boolean;
}) {
  const inputId = useId();

  return (
    <label
      htmlFor={inputId}
      {...stylex.props(cardStyles.card, isSelected ? cardStyles.borderSelected : cardStyles.borderDefault)}
    >
      <FlexCol sx={{ minWidth: 0 }}>
        <TextStdMd sx={{ mb: option.subtitle != null ? 0.5 : 0 }}>{option.title}</TextStdMd>
        {option.subtitle != null && <TextSmRg sx={{ color: cssVars.color.textSecondary }}>{option.subtitle}</TextSmRg>}
      </FlexCol>
      <Box sx={{ position: 'absolute', top: '4px', right: '8px' }}>
        <ManuallyLabeledRadio.Option id={inputId} value={option.value} />
      </Box>
    </label>
  );
}

const cardStyles = stylex.create({
  card: {
    position: 'relative',
    width: '100%',
    padding: spacer.spacer12px,
    borderRadius: borderRadius.medium,
    cursor: 'pointer',
    borderWidth: '1px',
    borderStyle: 'solid',
    transitionProperty: 'background-color',
    transitionDuration: '0.1s',
    transitionTimingFunction: 'ease-in-out',
    backgroundColor: {
      default: 'transparent',
      ':hover': token.bgHover,
    },
  },
  borderDefault: { borderColor: token.border },
  borderSelected: { borderColor: token.borderSelected },
});

const BlackRectangleButton = styled('button')({
  backgroundColor: 'transparent',
  border: '1px solid rgba(240, 240, 229, 0.2)',
  cursor: 'pointer',
  width: '100%',
  padding: '16px 24px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  '&:hover': {
    backgroundColor: 'rgba(240, 240, 229, 0.1)',
  },
});

function getButtonStyles(isSelected: boolean) {
  return {
    position: 'relative',
    backgroundColor: isSelected ? themeColor.white08_T : 'transparent',
    borderColor: isSelected ? themeColor.white64_T : '',
    '&:hover': {
      backgroundColor: isSelected ? themeColor.white16_T : token.bgTransparent,
    },
  };
}

function CheckmarkIcon({ isVisible }: { isVisible: boolean }) {
  return isVisible ? (
    <CheckMarkCircleFilledIcon
      title="checkmark"
      width={20}
      height={20}
      style={{
        color: themeColor.white100,
        position: 'absolute',
        top: 8,
        right: 8,
      }}
    />
  ) : null;
}
